// FollowManager.js
import { useState, useEffect } from 'react';
import { finalizeEvent } from 'nostr-tools/pure';
import { hexToBytes } from '@noble/hashes/utils';
import { useAuth } from './AuthContext';

export const useFollowManager = (relays) => {
  const [following, setFollowing] = useState(new Set());
  const [followers, setFollowers] = useState(new Set());
  const { publicKey, privateKey } = useAuth();

  // Fetch following list
  useEffect(() => {
	if (!publicKey) return;

	const fetchFollowing = async () => {
	  const followingSet = new Set();

	  for (const relay of relays) {
		const ws = new WebSocket(relay);
		
		try {
		  await new Promise((resolve) => {
			const timeout = setTimeout(() => {
			  ws.close();
			  resolve();
			}, 5000);

			ws.onopen = () => {
			  ws.send(JSON.stringify([
				"REQ",
				"following",
				{
				  kinds: [3],
				  authors: [publicKey]
				}
			  ]));
			};

			ws.onmessage = (event) => {
			  try {
				const [type, , data] = JSON.parse(event.data);
				if (type === "EVENT" && data.kind === 3) {
				  const contacts = JSON.parse(data.content);
				  Object.keys(contacts).forEach(pubkey => {
					followingSet.add(pubkey);
				  });
				}
				if (type === "EOSE") {
				  clearTimeout(timeout);
				  ws.close();
				  resolve();
				}
			  } catch (e) {
				console.error('Failed to parse following:', e);
			  }
			};
		  });
		} catch (error) {
		  console.error('Failed to fetch following from relay:', error);
		}
	  }

	  setFollowing(followingSet);
	};

	fetchFollowing();
  }, [publicKey, relays]);

  // Fetch followers
  useEffect(() => {
	if (!publicKey) return;

	const fetchFollowers = async () => {
	  const followersSet = new Set();

	  for (const relay of relays) {
		const ws = new WebSocket(relay);
		
		try {
		  await new Promise((resolve) => {
			const timeout = setTimeout(() => {
			  ws.close();
			  resolve();
			}, 5000);

			ws.onopen = () => {
			  ws.send(JSON.stringify([
				"REQ",
				"followers",
				{
				  kinds: [3],
				  "#p": [publicKey]
				}
			  ]));
			};

			ws.onmessage = (event) => {
			  try {
				const [type, , data] = JSON.parse(event.data);
				if (type === "EVENT" && data.kind === 3) {
				  followersSet.add(data.pubkey);
				}
				if (type === "EOSE") {
				  clearTimeout(timeout);
				  ws.close();
				  resolve();
				}
			  } catch (e) {
				console.error('Failed to parse followers:', e);
			  }
			};
		  });
		} catch (error) {
		  console.error('Failed to fetch followers from relay:', error);
		}
	  }

	  setFollowers(followersSet);
	};

	fetchFollowers();
  }, [publicKey, relays]);

  const follow = async (pubkeyToFollow) => {
	if (!publicKey || !privateKey) return false;

	try {
	  const newFollowing = new Set(following);
	  newFollowing.add(pubkeyToFollow);

	  const event = {
		kind: 3,
		pubkey: publicKey,
		created_at: Math.floor(Date.now() / 1000),
		tags: Array.from(newFollowing).map(key => ["p", key]),
		content: JSON.stringify(Object.fromEntries(
		  Array.from(newFollowing).map(key => [key, {}])
		))
	  };

	  const signedEvent = finalizeEvent(event, hexToBytes(privateKey));
	  let published = false;

	  for (const relay of relays) {
		const ws = new WebSocket(relay);
		
		try {
		  await new Promise((resolve) => {
			const timeout = setTimeout(() => {
			  ws.close();
			  resolve();
			}, 5000);

			ws.onopen = () => {
			  ws.send(JSON.stringify(["EVENT", signedEvent]));
			};

			ws.onmessage = (msg) => {
			  const [type, , status] = JSON.parse(msg.data);
			  if (type === "OK" && status === true) {
				published = true;
				clearTimeout(timeout);
				ws.close();
				resolve();
			  }
			};
		  });
		} catch (error) {
		  console.error('Failed to publish to relay:', error);
		}
	  }

	  if (published) {
		setFollowing(newFollowing);
		return true;
	  }
	} catch (error) {
	  console.error('Failed to follow:', error);
	}
	return false;
  };

  const unfollow = async (pubkeyToUnfollow) => {
	if (!publicKey || !privateKey) return false;

	try {
	  const newFollowing = new Set(following);
	  newFollowing.delete(pubkeyToUnfollow);

	  const event = {
		kind: 3,
		pubkey: publicKey,
		created_at: Math.floor(Date.now() / 1000),
		tags: Array.from(newFollowing).map(key => ["p", key]),
		content: JSON.stringify(Object.fromEntries(
		  Array.from(newFollowing).map(key => [key, {}])
		))
	  };

	  const signedEvent = finalizeEvent(event, hexToBytes(privateKey));
	  let published = false;

	  for (const relay of relays) {
		const ws = new WebSocket(relay);
		
		try {
		  await new Promise((resolve) => {
			const timeout = setTimeout(() => {
			  ws.close();
			  resolve();
			}, 5000);

			ws.onopen = () => {
			  ws.send(JSON.stringify(["EVENT", signedEvent]));
			};

			ws.onmessage = (msg) => {
			  const [type, , status] = JSON.parse(msg.data);
			  if (type === "OK" && status === true) {
				published = true;
				clearTimeout(timeout);
				ws.close();
				resolve();
			  }
			};
		  });
		} catch (error) {
		  console.error('Failed to publish to relay:', error);
		}
	  }

	  if (published) {
		setFollowing(newFollowing);
		return true;
	  }
	} catch (error) {
	  console.error('Failed to unfollow:', error);
	}
	return false;
  };

  return {
	following,
	followers,
	follow,
	unfollow,
	isFollowing: (pubkey) => following.has(pubkey)
  };
};